/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, KeyboardEvent, useEffect } from 'react';
import GenericModal from './GenericModal';

export interface GenericModalTriggerProps {
  trigger: React.ReactNode;
  title?: string;
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  isModalOpenProp?: boolean;
}

const GenericModalTrigger: React.FC<GenericModalTriggerProps> = ({ trigger, title = '', children, isModalOpenProp }) => {
  const [isOpen, setIsOpen] = useState(false);
  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  useEffect(() => {
    setIsOpen(isModalOpenProp ?? false);
  }, [isModalOpenProp]);

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openModal();
    }
  };

  let renderedTrigger: React.ReactElement;
  if (React.isValidElement(trigger)) {
    renderedTrigger = React.cloneElement(trigger as any, {
      onClick: openModal,
      onKeyDown: handleKeyDown,
      'aria-haspopup': 'dialog',
      'aria-expanded': isOpen,
    });
  } else {
    renderedTrigger = (
      <span
        onClick={openModal}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        style={{ display: 'inline-block', cursor: 'pointer' }}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        {trigger}
      </span>
    );
  }

  return (
    <>
      {renderedTrigger}
      <GenericModal isOpen={isOpen} onClose={closeModal} title={title}>
        {typeof children === 'function' ? children(closeModal) : children}
      </GenericModal>
    </>
  );
};

GenericModalTrigger.defaultProps = {
  title: '',
};

export default GenericModalTrigger;
